import React, { useEffect, useState, startTransition, useRef } from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { Formik } from "formik"
import { useFlexSearch } from "react-use-flexsearch"
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl"
import groupBy from "lodash-es/groupBy"
import sortBy from "lodash-es/sortBy"
import axios from "axios"
import { useQueryParamString } from "react-use-query-param-string"
import { graphql, useStaticQuery } from "gatsby"
import { useMedia } from "react-use"

import { Row, Col } from "../components/Grid"
import { ButtonRow, Form, Input } from "../components/FormFields"
import { TextButton } from "../components/Button"
import ImageContentful from "../components/ImageContentful"
import Link from "../components/Link"

import SpinnerSVG from "../images/icons/spinner.inline.svg"
import SearchIconSVG from "../images/icons/search-large.inline.svg"
import { getHighlightedText } from "../helpers"
import { media, mixins } from "../styles"

const SearchWrapper = styled.div``

const SearchInput = styled(Input)`
  ${mixins.copy1}
  margin: 0 0 50px;
`

const SearchIcon = styled.span`
  bottom: 4px;
  display: block;
  height: 18px;
  position: relative;
  width: 18px;

  @media ${media.xl} {
    bottom: 13px;
    height: 24px;
    width: 24px;
  }

  svg {
    height: auto;
    width: 100%;
  }
`

const ClearButton = styled(TextButton)`
  color: var(--color-sun);
  margin: -70px 0 0 auto;

  ${mixins.copy6}
`

const CategoryHeader = styled.h2`
  ${mixins.title6}
  border-bottom: 1px solid var(--color-greyLight);
  padding: 15px 0;
  margin: 0 0 30px;
`

const ResultList = styled(Row).attrs((props) => ({ as: "ul" }))`
  padding: 0;
`

const ResultItem = styled(Col).attrs((props) => ({ as: "li" }))`
  ${mixins.copy5}
  list-style: none;
  margin: 0 0 30px;
  padding: 0 ${({ theme }) => theme.styledBootstrapGrid.col.padding}px;

  .highlightText {
    background: transparent;
    color: var(--color-spaceblue);
    font-weight: 700;
  }

  a {
    color: var(--color-sage);
    text-decoration: none;
  }

  a > span {
    display: inline-flex;
    flex-flow: row nowrap;
  }
`

const ResultLink = styled(Link)`
  border-radius: 9px;
  display: block;
  outline-offset: 10px;

  &:focus {
    outline: 2px solid var(--color-sun);
  }
`

const ResultImage = styled.div`
  align-items: center;
  background-color: var(--color-greyLight);
  border-radius: 6px;
  flex: 1 0 40px;
  height: 40px;
  margin: 0 15px 0 0;
  overflow: hidden;
  position: relative;
  transform: translateZ(0); // Safari
  width: 40px;

  .wrap--svg,
  img,
  svg {
    display: block;
  }

  @media ${media.md} {
    border-radius: 10px;
    flex: 1 0 75px;
    height: 75px;
    width: 75px;
  }
`

const ResultIcon = styled.div`
  align-items: center;
  bottom: 0;
  color: var(--color-spaceblue);
  display: flex;
  justify-content: center;
  left: 0;
  padding: 3px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;

  ${(props) =>
    props.$overlay &&
    css`
      background: rgba(var(--color-spaceblueRGB), 0.4);
      color: var(--color-white);
    `}

  .wrap--svg,
  img,
  svg {
    display: block;
    height: auto;
    max-width: 100%;
  }
`

const ResultContent = styled.div``

const ResultTitle = styled.h3`
  color: var(--color-sun);
  font-weight: 400;
  margin: 1px 0 5px;
`

const ResultPath = styled.small`
  color: var(--color-spaceblue);
  display: block;
  line-height: 1.2;
  margin: 5px 0 0;
  opacity: 0.8;
`

const NoResults = styled.div`
  margin: 0 0 15px;
  overflow-wrap: break-word;

  p {
    ${mixins.copy4}
  }
`

const regexRootPage = new RegExp(/^\/(blog|it|fr|es)\//)

const ResultSection = ({
  header,
  display,
  imageType,
  results,
  query,
  domain,
  supportUrlQuery,
}) => {
  const intl = useIntl()

  const updatedResults = results.map((result) => {
    // We need to give it any valid URL so it can parse it.
    // We remove it before it goes out.
    const url = new URL(`${result.path}`, "https://x.x")
    if (supportUrlQuery) {
      url.search = `search=${query}`
    }

    return {
      ...result,
      pathWithQueryParam: url.toString().replace("https://x.x", ""),
    }
  })

  return (
    <>
      <CategoryHeader>{header}</CategoryHeader>
      <ResultList display={display}>
        {updatedResults.map((result) => (
          <ResultItem key={result.id} md={display === "twoCols" ? 6 : 12}>
            <ResultLink
              intl={false}
              tabIndex="0"
              to={
                result.isRoot || result.path.match(regexRootPage)
                  ? result.pathWithQueryParam
                  : `/${intl.locale}${result.pathWithQueryParam}`
              }
            >
              {imageType !== "none" ? (
                <ResultImage>
                  {result.icon ? (
                    <ResultIcon $overlay={result.icon && result.image}>
                      <ImageContentful
                        image={result.icon}
                        alt={result.title}
                        inline
                      />
                    </ResultIcon>
                  ) : null}
                  {result.image ? (
                    <ImageContentful image={result.image} alt={result.title} />
                  ) : null}
                </ResultImage>
              ) : null}
              <ResultContent>
                <ResultTitle>{result.title}</ResultTitle>
                {result.description ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedText(result.description, query),
                    }}
                  />
                ) : process.env.GATSBY_DEBUG_SEARCH === "true" ? (
                  "⚠️ Description missing!"
                ) : null}
                {domain && (
                  <ResultPath>
                    {domain}
                    {result.isRoot || result.path.match(regexRootPage)
                      ? ""
                      : `/${intl.locale}`}
                    {result.path}
                  </ResultPath>
                )}
              </ResultContent>
            </ResultLink>
          </ResultItem>
        ))}
      </ResultList>
    </>
  )
}

const SearchResults = ({ query, index, store, domain, supportUrlQuery }) => {
  const results = useFlexSearch(query, index, store)
  const intl = useIntl()

  const categories = [
    {
      name: "Services",
      display: "twoCols",
      header: intl.formatMessage({ id: "search.categories.services" }),
    },
    {
      name: "Industries",
      display: "twoCols",
      header: intl.formatMessage({ id: "search.categories.industries" }),
    },
    {
      name: "Case Studies",
      display: "twoCols",
      header: intl.formatMessage({ id: "search.categories.caseStudies" }),
    },
    {
      name: "Guides & Studies",
      display: "twoCols",
      header: intl.formatMessage({ id: "search.categories.guidesAndStudies" }),
    },
    {
      name: "Webinars",
      display: "twoCols",
      header: intl.formatMessage({ id: "search.categories.webinars" }),
      limit: 2,
    },
    {
      name: "Blog Posts",
      header: intl.formatMessage({ id: "search.categories.blogPosts" }),
      limit: 3,
    },
    {
      name: "FAQs",
      header: intl.formatMessage({ id: "search.categories.faqs" }),
      limit: 5,
    },
    {
      name: "Support Pages",
      header: intl.formatMessage({ id: "search.categories.supportPages" }),
      limit: 5,
    },
  ]

  const topResults = results
    .filter((result) => result.type === "Pages")
    .sort((a, b) => b.priority - a.priority)
  const missingInfo = results.filter((result) => !result.description)

  const sortedResults = results
    ? sortBy(Object.entries(groupBy(results, "type")), 0)
    : []

  if (results.length === 0) {
    return (
      <NoResults>
        <p>
          <FormattedMessage
            id="search.noResultsFor"
            values={{
              searchTerm: query,
            }}
          />
        </p>
      </NoResults>
    )
  }

  return (
    <>
      {topResults.length >= 1 && (
        <ResultSection
          header={intl.formatMessage({ id: "search.categories.topResults" })}
          imageType="none"
          results={topResults.slice(0, 3)}
          query={query}
          showPath={true}
          domain={domain}
          supportUrlQuery={supportUrlQuery}
        />
      )}
      {sortedResults.length >= 1 &&
        categories.map((category) =>
          sortedResults.filter(
            (resultCategory) => resultCategory[0] === category.name
          ).length > 0
            ? sortedResults
                .filter((resultCategory) => resultCategory[0] === category.name)
                .map((resultCategory) => (
                  <ResultSection
                    key={resultCategory[0]}
                    display={category.display}
                    header={category.header}
                    results={
                      category.limit
                        ? resultCategory[1].slice(0, category.limit)
                        : resultCategory[1]
                    }
                    query={query}
                    supportUrlQuery={supportUrlQuery}
                  />
                ))
            : null
        )}
      {process.env.GATSBY_DEBUG_SEARCH === "true" && missingInfo.length >= 1 ? (
        <ResultSection
          header="Debug: Missing description"
          imageType="none"
          results={missingInfo}
          query={query}
          showPath={true}
          domain={domain}
          supportUrlQuery={supportUrlQuery}
        />
      ) : null}
    </>
  )
}

const Search = ({ indexUrl, storeUrl, supportUrlQuery }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [query, setQuery] = useState("")
  const [urlQuery, setUrlQuery, initialized] = useQueryParamString("search", "")
  const [loaded, setLoaded] = useState(false)
  const [searchIndex, setSearchIndex] = useState("")
  const [searchStore, setSearchStore] = useState({})
  const minSearchLength = 3
  const searchFieldRef = useRef()
  const intl = useIntl()
  const isXL = useMedia(media.xl, false)

  const data = useStaticQuery(graphql`
    query SearchComponentQuery {
      site {
        siteMetadata {
          domain
        }
      }
    }
  `)

  // Function to lazy load the search index
  async function setupSearch() {
    const getSearchData = async function () {
      try {
        const [{ data: index }, { data: store }] = await Promise.all([
          axios.get(indexUrl),
          axios.get(storeUrl),
        ])
        setSearchIndex(JSON.stringify(index))
        setSearchStore(store)
        setLoaded(true)
      } catch (error) {
        console.log(error)
      }
    }
    getSearchData()
  }

  // Update the search term from input
  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  // Clear the search term and focus back to the input
  const clearSearch = () => {
    setSearchTerm("")
    searchFieldRef.current.focus()
  }

  // Setup the search index and focus the input
  useEffect(() => {
    setupSearch()
    searchFieldRef.current.focus()

    // We need the next line because eslint wants us to include setupSearch, which causes an endless loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set the initial search term based on the URL query parameter
  useEffect(() => {
    supportUrlQuery && setSearchTerm(urlQuery)
  }, [supportUrlQuery, urlQuery])

  // Update the search results lazily based on the input. We separate
  // `searchTerm` from `query` to prevent lagging the input field.
  useEffect(() => {
    startTransition(() => {
      setQuery(searchTerm)
      if (supportUrlQuery && urlQuery !== searchTerm) {
        setUrlQuery(searchTerm)
      }
    })
    // We need the next line because eslint wants us to include urlQuery, which causes an endless loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, supportUrlQuery, setQuery, setUrlQuery])

  return (
    <SearchWrapper data-testid="search-wrapper">
      <Formik>
        {() => (
          <Form
            aria-label={intl.formatMessage({ id: "search.ariaLabel" })}
            onSubmit={(e) => e.preventDefault()}
            role="search"
          >
            <SearchInput
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              name="searchTerm"
              iconLeft={
                <SearchIcon width={isXL ? 32 : 20}>
                  <SearchIconSVG />
                </SearchIcon>
              }
              label={intl.formatMessage({ id: "search.placeholder" })}
              hideLabel
              forwardRef={searchFieldRef}
              placeholder={intl.formatMessage({
                id: "search.placeholder",
              })}
              onChange={handleSearch}
              value={searchTerm}
            />

            {searchTerm?.length > 0 && (
              <ButtonRow>
                <ClearButton onClick={clearSearch} type="button">
                  {intl.formatMessage({
                    id: "search.clear",
                  })}
                </ClearButton>
              </ButtonRow>
            )}
          </Form>
        )}
      </Formik>
      {query?.length >= minSearchLength ? (
        initialized && loaded ? (
          <SearchResults
            domain={data.site.siteMetadata.domain}
            query={query}
            index={searchIndex}
            store={searchStore}
            supportUrlQuery={supportUrlQuery}
          />
        ) : (
          <span className="spinner">
            <SpinnerSVG />
          </span>
        )
      ) : null}
    </SearchWrapper>
  )
}

Search.propTypes = {
  indexUrl: PropTypes.string.isRequired,
  storeUrl: PropTypes.string.isRequired,
  supportUrlQuery: PropTypes.bool,
}

export default Search
